export type FpsHitFeedbackKind = 'shield' | 'body' | 'crit' | 'headshot' | 'elimination';

type DamageKind = Exclude<FpsHitFeedbackKind, 'elimination'>;

type FloatingDamage = {
  el: HTMLDivElement;
  age: number;
  life: number;
  x: number;
  y: number;
  active: boolean;
};

type IncomingMarker = {
  el: HTMLDivElement;
  age: number;
  life: number;
  angle: number;
  intensity: number;
  mode: 'damage' | 'suppression';
  active: boolean;
};

type DirectionArcState = {
  angle: number;
  intensity: number;
  age: number;
  life: number;
  active: boolean;
};

const HIT_COLORS: Record<FpsHitFeedbackKind, string> = {
  shield: '#42d7ff',
  body: '#ffffff',
  crit: '#ffd700',
  headshot: '#ff303d',
  elimination: '#ff303d',
};

const DAMAGE_COLORS: Record<DamageKind, string> = {
  shield: '#42d7ff',
  body: '#ffffff',
  crit: '#ffd700',
  headshot: '#ff303d',
};

const HIT_KIND_PRIORITY: Record<FpsHitFeedbackKind, number> = {
  body: 0,
  shield: 1,
  crit: 2,
  headshot: 3,
  elimination: 4,
};

const HIT_THROTTLE_S = 0.055;

export class FpsHitFeedback {
  private readonly layer: HTMLDivElement;
  private readonly style: HTMLStyleElement;
  private readonly hitMarker: HTMLDivElement;
  private readonly damagePool: FloatingDamage[] = [];
  private readonly incomingPool: IncomingMarker[] = [];
  private readonly directionArc: HTMLDivElement;
  private readonly directionArcPath: SVGPathElement;
  private directionArcState: DirectionArcState = {
    angle: 0,
    intensity: 1,
    age: 0,
    life: 0,
    active: false,
  };
  private hitAge = 999;
  private hitLife = 0.22;
  private hitKind: FpsHitFeedbackKind = 'body';
  private hitStack = 0;
  private disposed = false;
  private readonly prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  private reducedMotion = this.prefersReducedMotion;
  private intensity = 1;

  constructor(private readonly root: HTMLElement) {
    this.style = document.createElement('style');
    this.style.textContent = `
.fps-hit-feedback-layer{position:fixed;inset:0;z-index:71;pointer-events:none;overflow:hidden;contain:layout style paint}
.fps-hit-feedback-marker{position:absolute;left:50%;top:50%;width:44px;height:44px;transform:translate(-50%,-50%) scale(.82);opacity:0;will-change:transform,opacity,filter}
.fps-hit-feedback-marker::before,.fps-hit-feedback-marker::after{content:"";position:absolute;left:50%;top:50%;background:var(--fps-hit-color,#fff);border-radius:999px;box-shadow:0 0 10px var(--fps-hit-color,#fff),0 0 2px #000}
.fps-hit-feedback-marker::before{width:3px;height:20px;transform:translate(-50%,-50%)}
.fps-hit-feedback-marker::after{width:20px;height:3px;transform:translate(-50%,-50%)}
.fps-hit-feedback-marker[data-kind="headshot"]::before,.fps-hit-feedback-marker[data-kind="elimination"]::before{height:26px}
.fps-hit-feedback-marker[data-kind="headshot"]::after,.fps-hit-feedback-marker[data-kind="elimination"]::after{width:26px}
.fps-hit-feedback-marker[data-kind="crit"]::before{height:22px;width:3px}
.fps-hit-feedback-marker[data-kind="crit"]::after{width:22px;height:3px}
.fps-hit-feedback-damage{position:absolute;left:0;top:0;min-width:28px;text-align:center;font:900 22px/1 "Trebuchet MS","Segoe UI",system-ui,sans-serif;letter-spacing:.03em;text-shadow:0 2px 5px #000,0 0 2px #000,0 0 12px currentColor;will-change:transform,opacity;opacity:0}
.fps-hit-feedback-damage[data-kind="headshot"]{font-size:28px;letter-spacing:.06em}
.fps-hit-feedback-damage[data-kind="crit"]{font-size:34px;letter-spacing:.05em;font-weight:900;text-shadow:0 0 18px #ffd700,0 0 8px #ffb300,0 2px 6px #000,0 0 2px #000}
.fps-hit-feedback-incoming{position:absolute;left:50%;top:50%;width:220px;height:220px;margin:-110px 0 0 -110px;opacity:0;will-change:transform,opacity;transform-origin:center}
.fps-hit-feedback-incoming::after{content:"";position:absolute;left:50%;top:2px;width:54px;height:54px;transform:translateX(-50%) scale(var(--fps-incoming-mark-scale,1));border-radius:999px;background:radial-gradient(circle,rgba(0,0,0,.34),transparent 64%);filter:blur(1px)}
.fps-hit-feedback-incoming::before{content:"";position:absolute;left:50%;top:8px;width:0;height:0;transform:translateX(-50%) scale(var(--fps-incoming-mark-scale,1));border-left:20px solid transparent;border-right:20px solid transparent;border-bottom:42px solid rgba(255,45,54,var(--fps-incoming-alpha,.95));filter:drop-shadow(0 0 var(--fps-incoming-glow,12px) rgba(255,30,38,.96)) drop-shadow(0 0 2px #fff) drop-shadow(0 2px 3px #000);z-index:1}
.fps-hit-feedback-incoming[data-mode="suppression"]::after{top:-1px;width:48px;height:48px;background:radial-gradient(circle,rgba(255,255,255,.18),transparent 68%)}
.fps-hit-feedback-incoming[data-mode="suppression"]::before{top:10px;border-left-width:16px;border-right-width:16px;border-bottom-width:34px;border-bottom-color:rgba(255,236,190,var(--fps-incoming-alpha,.5));filter:drop-shadow(0 0 9px rgba(255,216,150,.48)) drop-shadow(0 1px 3px #000)}
.fps-hit-feedback-direction-arc{position:absolute;left:50%;top:50%;width:min(92vmin,640px);height:min(92vmin,640px);margin:-46vmin 0 0 -46vmin;pointer-events:none;opacity:0;will-change:opacity,transform;transform-origin:center}
.fps-hit-feedback-direction-arc svg{width:100%;height:100%;overflow:visible}
.fps-hit-feedback-direction-arc path{fill:rgba(255,42,54,var(--fps-dir-arc-alpha,.72));filter:drop-shadow(0 0 14px rgba(255,36,48,.55)) drop-shadow(0 0 2px rgba(255,255,255,.35))}
@media (max-width:720px),(max-height:520px){.fps-hit-feedback-marker{width:32px;height:32px}.fps-hit-feedback-marker::before{height:15px;width:2px}.fps-hit-feedback-marker::after{width:15px;height:2px}.fps-hit-feedback-marker[data-kind="headshot"]::before,.fps-hit-feedback-marker[data-kind="elimination"]::before{height:20px}.fps-hit-feedback-marker[data-kind="headshot"]::after,.fps-hit-feedback-marker[data-kind="elimination"]::after{width:20px}.fps-hit-feedback-damage{font-size:17px;min-width:22px}.fps-hit-feedback-damage[data-kind="headshot"]{font-size:21px}.fps-hit-feedback-damage[data-kind="crit"]{font-size:26px}.fps-hit-feedback-incoming{width:152px;height:152px;margin:-76px 0 0 -76px}.fps-hit-feedback-incoming::after{top:0;width:38px;height:38px}.fps-hit-feedback-incoming::before{top:6px;border-left-width:14px;border-right-width:14px;border-bottom-width:29px}}
@media (prefers-reduced-motion:reduce){.fps-hit-feedback-marker,.fps-hit-feedback-damage,.fps-hit-feedback-incoming{transition:none!important}}
`;
    document.head.appendChild(this.style);

    this.layer = document.createElement('div');
    this.layer.className = 'fps-hit-feedback-layer';
    this.root.appendChild(this.layer);

    this.hitMarker = document.createElement('div');
    this.hitMarker.className = 'fps-hit-feedback-marker';
    this.layer.appendChild(this.hitMarker);

    this.directionArc = document.createElement('div');
    this.directionArc.className = 'fps-hit-feedback-direction-arc';
    this.directionArc.innerHTML =
      '<svg viewBox="-100 -100 200 200" aria-hidden="true"><path id="fps-dir-arc-path" d=""></path></svg>';
    this.directionArcPath = this.directionArc.querySelector('#fps-dir-arc-path') as SVGPathElement;
    this.layer.appendChild(this.directionArc);
  }

  showHit(kind: FpsHitFeedbackKind): void {
    if (this.disposed) return;
    const throttled = this.hitAge < HIT_THROTTLE_S && kind !== 'elimination';
    if (throttled) {
      if (HIT_KIND_PRIORITY[kind] >= HIT_KIND_PRIORITY[this.hitKind]) {
        this.hitKind = kind;
        this.hitMarker.dataset.kind = kind;
        this.hitMarker.style.setProperty('--fps-hit-color', HIT_COLORS[kind]);
      }
      this.hitStack = Math.min(6, this.hitStack + 1);
      return;
    }
    this.hitKind = kind;
    this.hitAge = 0;
    this.hitStack = 0;
    const baseLife =
      kind === 'elimination' ? 0.34 : kind === 'headshot' ? 0.28 : kind === 'crit' ? 0.24 : 0.22;
    this.hitLife = baseLife * (this.reducedMotion ? 0.82 : 1);
    this.hitMarker.dataset.kind = kind;
    this.hitMarker.style.setProperty('--fps-hit-color', HIT_COLORS[kind]);
  }

  showDamage(amount: number, kind: DamageKind, x?: number, y?: number): void {
    if (this.disposed) return;
    const item = this.getDamageItem();
    const baseX = x ?? window.innerWidth * 0.5 + (Math.random() - 0.5) * 42;
    const baseY = y ?? window.innerHeight * 0.46 + (Math.random() - 0.5) * 24;
    item.age = 0;
    const constrained = window.innerWidth <= 720 || window.innerHeight <= 520;
    item.life =
      (kind === 'headshot' ? 0.86 : kind === 'crit' ? 0.92 : 0.68) *
      (this.reducedMotion ? 0.82 : 1) *
      (constrained ? 0.86 : 1);
    item.x = baseX;
    item.y = baseY;
    item.active = true;
    item.el.dataset.kind = kind;
    item.el.textContent = `${kind === 'headshot' ? '!' : kind === 'crit' ? '★' : ''}${Math.round(amount)}`;
    item.el.style.color = DAMAGE_COLORS[kind];
    item.el.style.display = 'block';
  }

  showIncoming(angleRad: number, intensity: number): void {
    if (this.disposed) return;
    const item = this.getIncomingItem();
    item.age = 0;
    const adjustedIntensity = intensity * this.intensity;
    item.life = (0.72 + Math.max(0, Math.min(0.18, (adjustedIntensity - 0.9) * 0.16))) * (this.reducedMotion ? 0.82 : 1);
    item.angle = angleRad;
    item.intensity = Math.max(0.25, Math.min(1.4, adjustedIntensity));
    item.mode = 'damage';
    item.active = true;
    item.el.dataset.mode = 'damage';
    item.el.style.display = 'block';
    this.showDirectionArc(angleRad, adjustedIntensity, item.life);
  }

  showSuppression(angleRad: number, intensity: number): void {
    if (this.disposed) return;
    const item = this.getIncomingItem();
    item.age = 0;
    const adjustedIntensity = intensity * this.intensity;
    item.life = (0.36 + Math.max(0, Math.min(0.12, adjustedIntensity * 0.12))) * (this.reducedMotion ? 0.75 : 1);
    item.angle = angleRad;
    item.intensity = Math.max(0.18, Math.min(0.62, adjustedIntensity));
    item.mode = 'suppression';
    item.active = true;
    item.el.dataset.mode = 'suppression';
    item.el.style.display = 'block';
  }

  setReducedMotion(on: boolean): void {
    this.reducedMotion = this.prefersReducedMotion || on;
  }

  setIntensity(multiplier: number): void {
    this.intensity = Math.max(0.75, Math.min(1.2, multiplier));
  }

  /** Screen-edge arc wedge pointing toward incoming damage (25X phase 39). */
  showDirectionArc(angleRad: number, intensity: number, life = 0.85): void {
    if (this.disposed) return;
    const adjusted = intensity * this.intensity;
    this.directionArcState.angle = angleRad;
    this.directionArcState.intensity = Math.max(0.3, Math.min(1.4, adjusted));
    this.directionArcState.age = 0;
    this.directionArcState.life =
      life * (this.reducedMotion ? 0.82 : 1) * (0.88 + Math.min(0.22, (adjusted - 0.5) * 0.18));
    this.directionArcState.active = true;
    this.directionArcPath.setAttribute('d', this.buildDirectionArcPath(this.directionArcState.intensity));
    this.directionArc.style.display = 'block';
  }

  update(dt: number): void {
    if (this.disposed) return;
    this.updateHitMarker(dt);
    this.updateDirectionArc(dt);
    for (const item of this.damagePool) this.updateDamage(item, dt);
    for (const item of this.incomingPool) this.updateIncoming(item, dt);
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.layer.remove();
    this.style.remove();
    this.damagePool.length = 0;
    this.incomingPool.length = 0;
  }

  private updateHitMarker(dt: number): void {
    this.hitAge += dt;
    const t = this.hitAge / this.hitLife;
    if (t >= 1) {
      this.hitMarker.style.opacity = '0';
      this.hitStack = 0;
      return;
    }
    const peak = t < 0.18 ? t / 0.18 : 1;
    const fade = 1 - t;
    const stackBoost = 1 + Math.min(0.22, this.hitStack * 0.04);
    const opacity = Math.min(1, peak * 1.25) * fade * this.intensity * stackBoost;
    const scalePulse = this.reducedMotion
      ? 0.04
      : (this.hitKind === 'elimination' ? 0.34 : this.hitKind === 'headshot' ? 0.26 : 0.2) * this.intensity;
    const scale = (0.92 + Math.sin(Math.min(1, t) * Math.PI) * scalePulse) * stackBoost;
    const rotate = this.hitKind === 'elimination' && !this.reducedMotion ? 45 : 0;
    this.hitMarker.style.opacity = String(Math.min(1, opacity));
    this.hitMarker.style.transform = `translate(-50%,-50%) rotate(${rotate}deg) scale(${scale})`;
    this.hitMarker.style.filter = `drop-shadow(0 0 ${this.hitKind === 'elimination' || this.hitKind === 'headshot' ? 16 : 10}px ${HIT_COLORS[this.hitKind]})`;
  }

  private updateDamage(item: FloatingDamage, dt: number): void {
    if (!item.active) return;
    item.age += dt;
    const t = item.age / item.life;
    if (t >= 1) {
      item.active = false;
      item.el.style.display = 'none';
      return;
    }
    const kind = item.el.dataset.kind as DamageKind | undefined;
    const critBoost = kind === 'crit' ? 1.28 : 1;
    const rise = (this.reducedMotion ? 24 : 62 * this.intensity * critBoost) * t;
    const scale =
      (kind === 'crit' ? 1.12 : 1) +
      (1 - Math.min(1, t * 2)) * (this.reducedMotion ? 0.08 : (kind === 'crit' ? 0.42 : 0.32) * this.intensity);
    const opacity = (t < 0.12 ? t / 0.12 : 1 - Math.max(0, (t - 0.58) / 0.42)) * this.intensity;
    item.el.style.opacity = String(Math.max(0, Math.min(1, opacity)));
    item.el.style.transform = `translate(${item.x}px,${item.y - rise}px) translate(-50%,-50%) scale(${scale})`;
  }

  private buildDirectionArcPath(intensity: number): string {
    const spread = 0.22 + intensity * 0.14;
    const innerR = 74;
    const outerR = 92 + intensity * 6;
    const center = -Math.PI / 2;
    const start = center - spread;
    const end = center + spread;
    const x0 = Math.cos(start) * innerR;
    const y0 = Math.sin(start) * innerR;
    const x1 = Math.cos(end) * innerR;
    const y1 = Math.sin(end) * innerR;
    const x2 = Math.cos(end) * outerR;
    const y2 = Math.sin(end) * outerR;
    const x3 = Math.cos(start) * outerR;
    const y3 = Math.sin(start) * outerR;
    const large = spread * 2 > Math.PI ? 1 : 0;
    return `M ${x0} ${y0} A ${innerR} ${innerR} 0 ${large} 1 ${x1} ${y1} L ${x2} ${y2} A ${outerR} ${outerR} 0 ${large} 0 ${x3} ${y3} Z`;
  }

  private updateDirectionArc(dt: number): void {
    if (!this.directionArcState.active) {
      this.directionArc.style.opacity = '0';
      return;
    }
    this.directionArcState.age += dt;
    const t = this.directionArcState.age / this.directionArcState.life;
    if (t >= 1) {
      this.directionArcState.active = false;
      this.directionArc.style.opacity = '0';
      this.directionArc.style.display = 'none';
      return;
    }
    const fade = t < 0.14 ? t / 0.14 : 1 - Math.max(0, (t - 0.22) / 0.78);
    const opacity = fade * this.directionArcState.intensity * this.intensity;
    this.directionArc.style.opacity = String(Math.max(0, Math.min(1, opacity)));
    this.directionArc.style.setProperty('--fps-dir-arc-alpha', String(0.55 + this.directionArcState.intensity * 0.28));
    this.directionArc.style.transform = `rotate(${(this.directionArcState.angle * 180) / Math.PI}deg) scale(${0.96 + (1 - t) * 0.06})`;
  }

  private updateIncoming(item: IncomingMarker, dt: number): void {
    if (!item.active) return;
    item.age += dt;
    const t = item.age / item.life;
    if (t >= 1) {
      item.active = false;
      item.el.style.display = 'none';
      return;
    }
    const fade = t < 0.18 ? 1 : 1 - Math.max(0, (t - 0.18) / 0.82);
    const opacity = Math.max(0, fade * item.intensity * (item.mode === 'suppression' ? 0.72 : 1));
    const cueScale = item.mode === 'damage' && item.intensity > 0.9 ? 1 + Math.min(0.18, (item.intensity - 0.9) * 0.22) : 1;
    const scale = this.reducedMotion ? cueScale : cueScale * (0.9 + (1 - t) * 0.18 * item.intensity);
    item.el.style.opacity = String(opacity);
    item.el.style.setProperty('--fps-incoming-alpha', String(item.mode === 'suppression' ? Math.min(0.58, 0.32 + item.intensity * 0.34) : Math.min(1, 0.78 + item.intensity * 0.18)));
    item.el.style.setProperty('--fps-incoming-glow', item.mode === 'damage' && item.intensity > 0.9 ? `${12 + (item.intensity - 0.9) * 12}px` : '12px');
    item.el.style.setProperty('--fps-incoming-mark-scale', String(item.mode === 'damage' ? cueScale : 0.9 + item.intensity * 0.12));
    item.el.style.transform = `rotate(${(item.angle * 180) / Math.PI}deg) scale(${scale})`;
  }

  private getDamageItem(): FloatingDamage {
    const inactive = this.damagePool.find((item) => !item.active);
    if (inactive) return inactive;
    const el = document.createElement('div');
    el.className = 'fps-hit-feedback-damage';
    el.style.display = 'none';
    this.layer.appendChild(el);
    const item: FloatingDamage = { el, age: 0, life: 0, x: 0, y: 0, active: false };
    this.damagePool.push(item);
    return item;
  }

  private getIncomingItem(): IncomingMarker {
    const inactive = this.incomingPool.find((item) => !item.active);
    if (inactive) return inactive;
    const el = document.createElement('div');
    el.className = 'fps-hit-feedback-incoming';
    el.style.display = 'none';
    this.layer.appendChild(el);
    const item: IncomingMarker = { el, age: 0, life: 0, angle: 0, intensity: 1, mode: 'damage', active: false };
    this.incomingPool.push(item);
    return item;
  }
}
